import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title,
  PieController
} from 'chart.js';

// Register Chart.js components including PieController
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, PieController);

class PDFReportGenerator {
  constructor(measurements, projectInfo, options = {}) {
    this.measurements = measurements;
    this.projectInfo = projectInfo;
    this.options = {
      companyName: 'TransTech Connect',
      companyLogo: null,
      qualityThresholds: {
        compaction: { pass: 92.0, warning: 88.0 },
        density: { pass: 145.0, warning: 140.0 }
      },
      includeMap: true,
      includePhotos: true,
      ...options
    };
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageHeight = 297;
    this.pageWidth = 210;
    this.margin = 20;
    this.currentY = this.margin;
    
    // Professional typography settings
    this.typography = {
      title: { size: 28, font: 'helvetica', weight: 'bold', lineHeight: 1.2 },
      subtitle: { size: 18, font: 'helvetica', weight: 'normal', lineHeight: 1.3 },
      sectionHeader: { size: 14, font: 'helvetica', weight: 'bold', lineHeight: 1.4 },
      body: { size: 11, font: 'helvetica', weight: 'normal', lineHeight: 1.5 },
      caption: { size: 9, font: 'helvetica', weight: 'normal', lineHeight: 1.4 },
      small: { size: 8, font: 'helvetica', weight: 'normal', lineHeight: 1.3 }
    };
    
    // Color palette
    this.colors = {
      primary: { r: 41, g: 98, b: 255 },      // Blue
      success: { r: 16, g: 185, b: 129 },     // Green
      warning: { r: 245, g: 158, b: 11 },     // Orange  
      danger: { r: 239, g: 68, b: 68 },       // Red
      gray: { r: 107, g: 114, b: 128 },       // Gray
      lightGray: { r: 248, g: 250, b: 252 },  // Light gray
      text: { r: 31, g: 41, b: 55 },          // Dark text
      textLight: { r: 107, g: 114, b: 128 }   // Light text
    };
  }

  // Apply typography style
  applyTypography(style) {
    const typo = this.typography[style];
    this.doc.setFontSize(typo.size);
    this.doc.setFont(typo.font, typo.weight);
    return typo.lineHeight * typo.size * 0.352778; // Convert to mm
  }

  // Apply color
  applyColor(colorName) {
    const color = this.colors[colorName];
    this.doc.setTextColor(color.r, color.g, color.b);
  }

  // Main method to generate complete report
  async generateReport() {
    try {
      this.addCoverPage();
      this.addExecutiveSummary();
      this.addProjectDetails();
      await this.addQualityAnalysis();
      this.addMeasurementTable();
      if (this.options.includeMap) {
        await this.addLocationMap();
      }
      if (this.options.includePhotos) {
        await this.addVisualDocumentation();
      }
      this.addOperatorPerformance();
      this.addDataSummary();
      
      // Add professional footer with page numbers
      const totalPages = this.doc.internal.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        this.doc.setPage(i);
        this.addPageFooter(i - 1, totalPages - 1);
      }
      
      return this.doc;
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw error;
    }
  }

  // Professional page footer
  addPageFooter(pageNum, totalPages) {
    const footerY = this.pageHeight - 15;
    
    // Footer line
    this.doc.setDrawColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin, footerY - 5, this.pageWidth - this.margin, footerY - 5);
    
    // Page number (right)
    this.applyTypography('caption');
    this.applyColor('textLight');
    this.doc.text(`Page ${pageNum} of ${totalPages}`, this.pageWidth - this.margin - 20, footerY);
    
    // Company name (left)
    this.doc.text(this.options.companyName, this.margin, footerY);
    
    // Reset colors
    this.applyColor('text');
  }

  // Enhanced cover page
  addCoverPage() {
    // Background accent
    this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.rect(0, 0, this.pageWidth, 80, 'F');
    
    // Main title
    this.applyTypography('title');
    this.applyColor('text');
    this.centerText('ASPHALT DENSITY', 40);
    this.centerText('MEASUREMENT REPORT', 55);
    
    // Project name in accent color
    this.applyTypography('subtitle');
    this.applyColor('primary');
    this.centerText(this.projectInfo.name || 'Project Report', 85);
    
    // Project details section
    this.currentY = 110;
    this.applyTypography('body');
    this.applyColor('text');
    
    const details = [
      `Location: ${this.projectInfo.location || 'Not specified'}`,
      `Date Range: ${this.getDateRange()}`,
      `Total Measurements: ${this.measurements.length}`,
      `Report Generated: ${new Date().toLocaleDateString()}`
    ];
    
    details.forEach(detail => {
      this.centerText(detail, this.currentY);
      this.currentY += this.applyTypography('body');
    });
    
    // Quality score highlight box
    const qualityScore = this.calculateQualityScore();
    const boxY = 160;
    const boxHeight = 35;
    
    // Box shadow effect
    this.doc.setFillColor(200, 200, 200);
    this.doc.rect(this.margin + 22, boxY + 2, this.pageWidth - (2 * this.margin) - 44, boxHeight, 'F');
    
    // Main box
    this.doc.setFillColor(this.colors.primary.r, this.colors.primary.g, this.colors.primary.b);
    this.doc.rect(this.margin + 20, boxY, this.pageWidth - (2 * this.margin) - 40, boxHeight, 'F');
    
    // Score text
    this.applyTypography('sectionHeader');
    this.doc.setTextColor(255, 255, 255);
    this.centerText(`OVERALL QUALITY SCORE: ${qualityScore}/100`, boxY + 20);
    
    // Company section at bottom
    this.currentY = 240;
    this.applyTypography('sectionHeader');
    this.applyColor('text');
    this.centerText(this.options.companyName, this.currentY);
    
    this.applyTypography('body');
    this.applyColor('textLight');
    this.centerText('Professional Asphalt Quality Management', this.currentY + 15);
    
    this.addNewPage();
  }

  // Enhanced executive summary
  addExecutiveSummary() {
    this.addSectionHeader('EXECUTIVE SUMMARY');
    
    const stats = this.calculateStatistics();
    
    // Metrics box with better styling
    this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), 50, 'F');
    
    // Box border
    this.doc.setDrawColor(this.colors.gray.r, this.colors.gray.g, this.colors.gray.b);
    this.doc.setLineWidth(0.5);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), 50);
    
    // Content with proper spacing
    this.currentY += 8;
    this.applyTypography('sectionHeader');
    this.applyColor('primary');
    this.addText('PROJECT METRICS', this.currentY);
    this.currentY += 5;
    
    this.applyTypography('body');
    this.applyColor('text');
    const metrics = [
      `Total Measurements: ${stats.total}`,
      `Measurement Period: ${this.getDateRange()}`,
      `Overall Pass Rate: ${stats.passRate}%`,
      `Average Density: ${stats.avgDensity.toFixed(1)} pcf`,
      `Average Compaction: ${stats.avgCompaction.toFixed(1)}%`
    ];
    
    metrics.forEach(metric => {
      this.addText(metric, this.currentY);
      this.currentY += this.applyTypography('body');
    });
    
    this.currentY += 20;
    
    // Quality distribution with proper spacing
    this.applyTypography('sectionHeader');
    this.applyColor('text');
    this.addText('QUALITY DISTRIBUTION', this.currentY);
    this.currentY += 10;
    
    // Quality stats with color coding and proper spacing
    this.applyTypography('body');
    this.applyColor('success');
    this.addText(`• Pass (>=${this.options.qualityThresholds.compaction.pass}%): ${stats.pass} measurements (${((stats.pass/stats.total)*100).toFixed(1)}%)`, this.currentY);
    this.currentY += this.applyTypography('body');
    
    this.applyColor('warning');
    this.addText(`• Warning (${this.options.qualityThresholds.compaction.warning}% - ${this.options.qualityThresholds.compaction.pass-0.1}%): ${stats.warning} measurements (${((stats.warning/stats.total)*100).toFixed(1)}%)`, this.currentY);
    this.currentY += this.applyTypography('body');
    
    this.applyColor('danger');
    this.addText(`• Fail (<${this.options.qualityThresholds.compaction.warning}%): ${stats.fail} measurements (${((stats.fail/stats.total)*100).toFixed(1)}%)`, this.currentY);
    this.currentY += this.applyTypography('body');
    
    this.applyColor('text');
    this.currentY += 15;
    
    // Statistical summary with better formatting
    this.applyTypography('sectionHeader');
    this.addText('STATISTICAL SUMMARY', this.currentY);
    this.currentY += 8;
    
    const densityStats = this.calculateParameterStatistics('Density');
    const compactionStats = this.calculateParameterStatistics('Compaction');
    
    this.applyTypography('body');
    this.addText(`Density Range: ${densityStats.min.toFixed(1)} - ${densityStats.max.toFixed(1)} pcf (std dev = ${densityStats.stdDev.toFixed(1)})`, this.currentY);
    this.currentY += this.applyTypography('body');
    
    this.addText(`Compaction Range: ${compactionStats.min.toFixed(1)} - ${compactionStats.max.toFixed(1)}% (std dev = ${compactionStats.stdDev.toFixed(1)}%)`, this.currentY);
    this.currentY += 20;
  }

  // Project details with better layout
  addProjectDetails() {
    this.addSectionHeader('PROJECT DETAILS');
    
    this.applyTypography('body');
    this.applyColor('text');
    
    const details = [
      { label: 'Project Name:', value: this.projectInfo.name || 'N/A' },
      { label: 'Location:', value: this.projectInfo.location || 'N/A' },
      { label: 'Contractor:', value: this.projectInfo.contractor || 'N/A' },
      { label: 'Project Manager:', value: this.projectInfo.manager || 'N/A' },
      { label: 'Measurement Period:', value: this.getDateRange() },
      { label: 'Total Measurements:', value: this.measurements.length.toString() },
      { label: 'Equipment Used:', value: this.getEquipmentUsed() },
      { label: 'Quality Standards:', value: `Compaction >=${this.options.qualityThresholds.compaction.pass}%, Density >=${this.options.qualityThresholds.density.pass} pcf` }
    ];
    
    details.forEach(detail => {
      // Label in bold
      this.applyTypography('body');
      this.doc.setFont('helvetica', 'bold');
      const labelWidth = this.doc.getTextWidth(detail.label + ' ');
      this.doc.text(detail.label, this.margin, this.currentY);
      
      // Value in normal weight
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(detail.value, this.margin + labelWidth, this.currentY);
      
      this.currentY += this.applyTypography('body') + 1;
    });
    
    this.currentY += 15;
  }

  // Enhanced quality analysis
  async addQualityAnalysis() {
    this.addSectionHeader('QUALITY ANALYSIS');
    
    const stats = this.calculateStatistics();
    
    // Professional quality distribution chart
    this.addEnhancedQualityChart(stats);
    
    this.currentY += 30;
    
    // Data analysis with better formatting
    this.applyTypography('sectionHeader');
    this.applyColor('text');
    this.addText('MEASUREMENT ANALYSIS', this.currentY);
    this.currentY += 10;
    
    // Time-based analysis in organized format
    const timeAnalysis = this.analyzeTimeDistribution();
    this.applyTypography('body');
    
    const analysisItems = [
      `Measurement span: ${timeAnalysis.totalDays} day${timeAnalysis.totalDays !== 1 ? 's' : ''}`,
      `Peak activity: ${timeAnalysis.peakDay} (${timeAnalysis.peakCount} measurements)`,
      `Daily average: ${timeAnalysis.avgPerDay.toFixed(1)} measurements per day`
    ];
    
    analysisItems.forEach(item => {
      this.addText(`• ${item}`, this.currentY);
      this.currentY += this.applyTypography('body');
    });
    
    this.currentY += 15;
    
    // Location coverage if available
    const locationStats = this.analyzeLocationCoverage();
    if (locationStats.hasCoordinates) {
      this.applyTypography('sectionHeader');
      this.addText('GEOGRAPHIC COVERAGE', this.currentY);
      this.currentY += 8;
      
      this.applyTypography('body');
      this.addText(`Coverage area: ${locationStats.latRange.toFixed(4)} x ${locationStats.lngRange.toFixed(4)} degrees`, this.currentY);
      this.currentY += this.applyTypography('body');
      
      this.addText(`Center coordinates: ${locationStats.centerLat.toFixed(6)}, ${locationStats.centerLng.toFixed(6)}`, this.currentY);
      this.currentY += this.applyTypography('body');
    }
    
    this.currentY += 20;
  }

  // Enhanced quality distribution chart
  addEnhancedQualityChart(stats) {
    this.applyTypography('sectionHeader');
    this.addText('Quality Distribution Overview', this.currentY);
    this.currentY += 12;
    
    const total = stats.total;
    const chartWidth = 150;
    const chartHeight = 8;
    const chartX = this.margin;
    const chartY = this.currentY;
    
    // Calculate widths
    const passWidth = (stats.pass / total) * chartWidth;
    const warningWidth = (stats.warning / total) * chartWidth;
    const failWidth = (stats.fail / total) * chartWidth;
    
    // Chart background
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(chartX, chartY, chartWidth, chartHeight, 'F');
    
    // Chart segments with shadows for depth
    let currentX = chartX;
    
    if (passWidth > 0) {
      this.doc.setFillColor(this.colors.success.r, this.colors.success.g, this.colors.success.b);
      this.doc.rect(currentX, chartY, passWidth, chartHeight, 'F');
      currentX += passWidth;
    }
    
    if (warningWidth > 0) {
      this.doc.setFillColor(this.colors.warning.r, this.colors.warning.g, this.colors.warning.b);
      this.doc.rect(currentX, chartY, warningWidth, chartHeight, 'F');
      currentX += warningWidth;
    }
    
    if (failWidth > 0) {
      this.doc.setFillColor(this.colors.danger.r, this.colors.danger.g, this.colors.danger.b);
      this.doc.rect(currentX, chartY, failWidth, chartHeight, 'F');
    }
    
    // Chart border
    this.doc.setDrawColor(this.colors.gray.r, this.colors.gray.g, this.colors.gray.b);
    this.doc.setLineWidth(0.5);
    this.doc.rect(chartX, chartY, chartWidth, chartHeight);
    
    this.currentY += chartHeight + 8;
    
    // Enhanced legend with better spacing
    this.applyTypography('caption');
    const legendItems = [
      { color: this.colors.success, text: `Pass: ${stats.pass} (${((stats.pass/total)*100).toFixed(1)}%)` },
      { color: this.colors.warning, text: `Warning: ${stats.warning} (${((stats.warning/total)*100).toFixed(1)}%)` },
      { color: this.colors.danger, text: `Fail: ${stats.fail} (${((stats.fail/total)*100).toFixed(1)}%)` }
    ];
    
    legendItems.forEach((item, index) => {
      // Color square
      this.doc.setFillColor(item.color.r, item.color.g, item.color.b);
      this.doc.rect(this.margin, this.currentY - 2, 3, 3, 'F');
      
      // Legend text
      this.doc.setTextColor(item.color.r, item.color.g, item.color.b);
      this.doc.text(item.text, this.margin + 6, this.currentY);
      
      this.currentY += this.applyTypography('caption') + 1;
    });
    
    this.applyColor('text');
    this.currentY += 5;
  }

  // Enhanced measurement table with better formatting
  addMeasurementTable() {
    this.addSectionHeader('MEASUREMENT DATA');
    
    // Table styling
    const headers = ['Date/Time', 'Location', 'Density (pcf)', 'Compaction (%)', 'Status', 'Operator'];
    const colWidths = [35, 30, 25, 25, 20, 30];
    const rowHeight = 6;
    
    // Header row with background
    this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), rowHeight + 2, 'F');
    
    // Header text
    this.applyTypography('caption');
    this.doc.setFont('helvetica', 'bold');
    this.applyColor('text');
    
    let xPos = this.margin + 2;
    headers.forEach((header, index) => {
      this.doc.text(header, xPos, this.currentY + 4);
      xPos += colWidths[index];
    });
    
    this.currentY += rowHeight + 4;
    
    // Table rows with alternating backgrounds
    this.applyTypography('caption');
    this.doc.setFont('helvetica', 'normal');
    
    const displayMeasurements = this.measurements
      .sort((a, b) => new Date(b.LocalTimeOfMeasurement) - new Date(a.LocalTimeOfMeasurement))
      .slice(0, 50);
    
    displayMeasurements.forEach((measurement, index) => {
      if (this.currentY > 260) {
        this.addNewPage();
        this.currentY = this.margin + 10;
        
        // Reprint headers on new page
        this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
        this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), rowHeight + 2, 'F');
        
        this.doc.setFont('helvetica', 'bold');
        xPos = this.margin + 2;
        headers.forEach((header, index) => {
          this.doc.text(header, xPos, this.currentY + 4);
          xPos += colWidths[index];
        });
        this.currentY += rowHeight + 4;
        this.doc.setFont('helvetica', 'normal');
      }
      
      // Alternating row backgrounds
      if (index % 2 === 1) {
        this.doc.setFillColor(252, 252, 252);
        this.doc.rect(this.margin, this.currentY - 1, this.pageWidth - (2 * this.margin), rowHeight, 'F');
      }
      
      const measurementDate = new Date(measurement.LocalTimeOfMeasurement);
      const status = this.getQualityStatus(measurement);
      
      const row = [
        `${measurementDate.toLocaleDateString()} ${measurementDate.toLocaleTimeString().slice(0, -3)}`,
        (measurement.Description1 || 'N/A').substring(0, 10),
        measurement.Density ? measurement.Density.toFixed(1) : 'N/A',
        measurement.Compaction ? measurement.Compaction.toFixed(1) : 'N/A',
        status,
        (measurement.operator_name || 'Unknown').substring(0, 8)
      ];
      
      xPos = this.margin + 2;
      row.forEach((cell, cellIndex) => {
        // Color-code status column
        if (cellIndex === 4) {
          if (status === 'Pass') this.applyColor('success');
          else if (status === 'Warning') this.applyColor('warning');
          else if (status === 'Fail') this.applyColor('danger');
          else this.applyColor('gray');
        } else {
          this.applyColor('text');
        }
        
        this.doc.text(String(cell), xPos, this.currentY + 3);
        xPos += colWidths[cellIndex];
      });
      
      this.currentY += rowHeight;
    });
    
    // Table note
    if (this.measurements.length > 50) {
      this.currentY += 8;
      this.applyTypography('caption');
      this.applyColor('textLight');
      this.doc.setFont('helvetica', 'italic');
      this.addText(`Note: Showing 50 most recent measurements of ${this.measurements.length} total`, this.currentY);
      this.doc.setFont('helvetica', 'normal');
    }
    
    this.applyColor('text');
    this.currentY += 20;
  }

  // Enhanced location map section
  async addLocationMap() {
    this.addSectionHeader('MEASUREMENT LOCATIONS');
    
    const measurementsWithCoords = this.measurements.filter(m => m.latitude && m.longitude);
    
    if (measurementsWithCoords.length === 0) {
      this.applyTypography('body');
      this.applyColor('textLight');
      this.addText('No geographic coordinates available for measurements.', this.currentY);
      this.currentY += 20;
      return;
    }
    
    // Calculate bounds
    const lats = measurementsWithCoords.map(m => m.latitude);
    const lngs = measurementsWithCoords.map(m => m.longitude);
    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    };
    
    // Location summary in a styled box
    this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), 40, 'F');
    
    this.currentY += 6;
    this.applyTypography('body');
    this.applyColor('text');
    
    const locationInfo = [
      `Total measurement locations: ${measurementsWithCoords.length}`,
      `Geographic bounds:`,
      `  North: ${bounds.north.toFixed(6)} degrees, South: ${bounds.south.toFixed(6)} degrees`,
      `  East: ${bounds.east.toFixed(6)} degrees, West: ${bounds.west.toFixed(6)} degrees`,
      `Coverage area: ${(bounds.north - bounds.south).toFixed(6)} x ${(bounds.east - bounds.west).toFixed(6)} degrees`
    ];
    
    locationInfo.forEach(info => {
      this.addText(info, this.currentY);
      this.currentY += this.applyTypography('body');
    });
    
    this.currentY += 10;
    
    // Map visualization note with simple text instead of emojis
    this.doc.setFillColor(this.colors.primary.r, this.colors.primary.g, this.colors.primary.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), 25, 'F');
    
    this.currentY += 6;
    this.applyTypography('body');
    this.doc.setTextColor(255, 255, 255);
    this.addText('MAP VISUALIZATION: Geographic distribution of measurement points', this.currentY);
    this.currentY += this.applyTypography('body');
    this.addText('DIGITAL PLATFORM: Interactive maps available online', this.currentY);
    
    this.applyColor('text');
    this.currentY += 25;
  }

  // Enhanced visual documentation
  async addVisualDocumentation() {
    this.addSectionHeader('VISUAL DOCUMENTATION');
    
    const photosWithMeasurements = this.measurements.filter(m => m.photo_path);
    
    if (photosWithMeasurements.length === 0) {
      this.applyTypography('body');
      this.applyColor('textLight');
      this.addText('No field photos available for this project.', this.currentY);
      this.currentY += 20;
      return;
    }
    
    // Photo summary
    this.applyTypography('body');
    this.applyColor('text');
    this.addText(`FIELD DOCUMENTATION: ${photosWithMeasurements.length} photos available`, this.currentY);
    this.currentY += 15;
    
    // Photo list with better formatting
    this.applyTypography('sectionHeader');
    this.addText('Photo Documentation:', this.currentY);
    this.currentY += 8;
    
    photosWithMeasurements.slice(0, 10).forEach((measurement, index) => {
      if (this.currentY > 240) {
        this.addNewPage();
        this.currentY = this.margin + 10;
      }
      
      const measurementDate = new Date(measurement.LocalTimeOfMeasurement);
      
      // Photo entry with number
      this.applyTypography('body');
      this.doc.setFont('helvetica', 'bold');
      this.applyColor('primary');
      this.addText(`${index + 1}.`, this.currentY);
      
      this.doc.setFont('helvetica', 'normal');
      this.applyColor('text');
      this.doc.text(`${measurement.Description1 || 'Measurement'} - ${measurementDate.toLocaleDateString()}`, this.margin + 8, this.currentY);
      this.currentY += this.applyTypography('body');
      
      // Notes in smaller text
      if (measurement.notes) {
        this.applyTypography('caption');
        this.applyColor('textLight');
        this.doc.setFont('helvetica', 'italic');
        const noteText = measurement.notes.length > 80 ? measurement.notes.substring(0, 80) + '...' : measurement.notes;
        this.addText(`   Notes: ${noteText}`, this.currentY);
        this.currentY += this.applyTypography('caption');
        this.doc.setFont('helvetica', 'normal');
      }
      
      this.currentY += 2;
    });
    
    // Note about additional photos
    if (photosWithMeasurements.length > 10) {
      this.currentY += 8;
      this.applyTypography('caption');
      this.applyColor('textLight');
      this.doc.setFont('helvetica', 'italic');
      this.addText(`Note: Showing 10 of ${photosWithMeasurements.length} photos. Full documentation available in digital platform.`, this.currentY);
      this.doc.setFont('helvetica', 'normal');
    }
    
    this.applyColor('text');
    this.currentY += 25;
  }

  // Enhanced operator performance
  addOperatorPerformance() {
    this.addSectionHeader('OPERATOR PERFORMANCE');
    
    const operatorStats = this.calculateOperatorStats();
    
    if (Object.keys(operatorStats).length === 0) {
      this.applyTypography('body');
      this.applyColor('textLight');
      this.addText('No operator data available.', this.currentY);
      this.currentY += 20;
      return;
    }
    
    // Sort operators by measurement count
    const sortedOperators = Object.entries(operatorStats)
      .sort(([,a], [,b]) => b.total - a.total);
    
    sortedOperators.forEach(([operator, stats]) => {
      if (this.currentY > 240) {
        this.addNewPage();
        this.currentY = this.margin + 10;
      }
      
      // Operator name with performance indicator
      this.applyTypography('sectionHeader');
      this.applyColor('text');
      this.addText(`${operator}:`, this.currentY);
      
      // Performance indicator
      const performanceColor = stats.passRate >= 90 ? 'success' : stats.passRate >= 70 ? 'warning' : 'danger';
      this.doc.setFillColor(this.colors[performanceColor].r, this.colors[performanceColor].g, this.colors[performanceColor].b);
      this.doc.circle(this.margin + this.doc.getTextWidth(`${operator}: `) + 5, this.currentY - 2, 1.5, 'F');
      
      this.currentY += 8;
      
      // Stats in organized format
      this.applyTypography('body');
      this.applyColor('text');
      
      const operatorDetails = [
        `Measurements: ${stats.total}`,
        `Pass Rate: ${stats.passRate}%`,
        `Average Density: ${stats.avgDensity.toFixed(1)} pcf`,
        `Average Compaction: ${stats.avgCompaction.toFixed(1)}%`
      ];
      
      operatorDetails.forEach(detail => {
        this.addText(`  • ${detail}`, this.currentY);
        this.currentY += this.applyTypography('body');
      });
      
      this.currentY += 8;
    });
    
    this.currentY += 15;
  }

  // Enhanced data summary
  addDataSummary() {
    this.addSectionHeader('DATA SUMMARY & OBSERVATIONS');
    
    const stats = this.calculateStatistics();
    
    // Summary section
    this.applyTypography('sectionHeader');
    this.applyColor('primary');
    this.addText('MEASUREMENT SUMMARY:', this.currentY);
    this.currentY += 10;
    
    this.applyTypography('body');
    this.applyColor('text');
    
    const observations = [
      `Total of ${stats.total} measurements collected over ${this.getDateRange()}`,
      `${stats.passRate}% of measurements meet quality standards (≥${this.options.qualityThresholds.compaction.pass}% compaction)`,
      `Density values range from ${this.getMinValue('Density').toFixed(1)} to ${this.getMaxValue('Density').toFixed(1)} pcf`,
      `Compaction values range from ${this.getMinValue('Compaction').toFixed(1)} to ${this.getMaxValue('Compaction').toFixed(1)}%`
    ];
    
    const operatorCount = Object.keys(this.calculateOperatorStats()).length;
    if (operatorCount > 0) {
      observations.push(`Data collected by ${operatorCount} operator${operatorCount > 1 ? 's' : ''}`);
    }
    
    const photosCount = this.measurements.filter(m => m.photo_path).length;
    if (photosCount > 0) {
      observations.push(`${photosCount} measurements include field photographs`);
    }
    
    observations.forEach(observation => {
      this.addText(`• ${observation}`, this.currentY);
      this.currentY += this.applyTypography('body') + 1;
    });
    
    this.currentY += 15;
    
    // Quality standards reference in a box
    this.doc.setFillColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - (2 * this.margin), 25, 'F');
    
    this.currentY += 6;
    this.applyTypography('sectionHeader');
    this.applyColor('primary');
    this.addText('QUALITY STANDARDS APPLIED:', this.currentY);
    this.currentY += 6;
    
    this.applyTypography('caption');
    this.applyColor('text');
    const standards = [
      `• Pass: Compaction >= ${this.options.qualityThresholds.compaction.pass}%, Density >= ${this.options.qualityThresholds.density.pass} pcf`,
      `• Warning: Compaction ${this.options.qualityThresholds.compaction.warning}% - ${this.options.qualityThresholds.compaction.pass - 0.1}%`,
      `• Fail: Compaction < ${this.options.qualityThresholds.compaction.warning}%`
    ];
    
    standards.forEach(standard => {
      this.addText(standard, this.currentY);
      this.currentY += this.applyTypography('caption');
    });
    
    this.currentY += 25;
    
    // Professional footer
    this.applyTypography('caption');
    this.applyColor('textLight');
    this.doc.setFont('helvetica', 'italic');
    this.centerText(`Report generated by ${this.options.companyName} on ${new Date().toLocaleDateString()}`, this.currentY);
    this.centerText('Data analysis based on collected field measurements', this.currentY + 8);
  }

  // Helper methods with improvements
  addSectionHeader(title) {
    if (this.currentY > 240) {
      this.addNewPage();
    }
    
    // Add extra space before section
    this.currentY += 5;
    
    this.applyTypography('sectionHeader');
    this.applyColor('text');
    this.doc.text(title, this.margin, this.currentY);
    
    // Decorative line under header
    const titleWidth = this.doc.getTextWidth(title);
    this.doc.setDrawColor(this.colors.primary.r, this.colors.primary.g, this.colors.primary.b);
    this.doc.setLineWidth(1);
    this.doc.line(this.margin, this.currentY + 3, this.margin + titleWidth, this.currentY + 3);
    
    // Light line extension
    this.doc.setDrawColor(this.colors.lightGray.r, this.colors.lightGray.g, this.colors.lightGray.b);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin + titleWidth, this.currentY + 3, this.pageWidth - this.margin, this.currentY + 3);
    
    this.currentY += 15;
  }

  addText(text, y, maxWidth = null) {
    if (y > 270) {
      this.addNewPage();
      y = this.margin + 10;
    }
    
    const textMaxWidth = maxWidth || (this.pageWidth - (2 * this.margin));
    const lines = this.doc.splitTextToSize(text, textMaxWidth);
    
    lines.forEach((line, index) => {
      const lineY = y + (index * this.applyTypography('body'));
      if (lineY > 270) {
        this.addNewPage();
        this.doc.text(line, this.margin, this.margin + 10 + (index * this.applyTypography('body')));
      } else {
        this.doc.text(line, this.margin, lineY);
      }
    });
    
    this.currentY = y + (lines.length * this.applyTypography('body'));
  }

  centerText(text, y) {
    const textWidth = this.doc.getTextWidth(text);
    const x = (this.pageWidth - textWidth) / 2;
    this.doc.text(text, x, y);
  }

  addNewPage() {
    this.doc.addPage();
    this.currentY = this.margin;
  }

  // All the calculation methods remain the same as before
  calculateStatistics() {
    const total = this.measurements.length;
    let pass = 0, warning = 0, fail = 0;
    let totalDensity = 0, totalCompaction = 0;
    let validDensity = 0, validCompaction = 0;
    
    this.measurements.forEach(m => {
      const status = this.getQualityStatus(m);
      if (status === 'Pass') pass++;
      else if (status === 'Warning') warning++;
      else if (status === 'Fail') fail++;
      
      if (m.Density !== null && m.Density !== undefined) {
        totalDensity += m.Density;
        validDensity++;
      }
      if (m.Compaction !== null && m.Compaction !== undefined) {
        totalCompaction += m.Compaction;
        validCompaction++;
      }
    });
    
    return {
      total,
      pass,
      warning,
      fail,
      passRate: total > 0 ? Math.round((pass / total) * 100) : 0,
      avgDensity: validDensity > 0 ? totalDensity / validDensity : 0,
      avgCompaction: validCompaction > 0 ? totalCompaction / validCompaction : 0
    };
  }

  calculateQualityScore() {
    const stats = this.calculateStatistics();
    const passRateScore = stats.passRate * 0.7;
    const consistencyScore = Math.max(0, 100 - (stats.fail / stats.total) * 300) * 0.3;
    return Math.round(passRateScore + consistencyScore);
  }

  calculateParameterStatistics(parameter) {
    const values = this.measurements
      .map(m => m[parameter])
      .filter(v => v !== null && v !== undefined && !isNaN(v));
    
    if (values.length === 0) {
      return { min: 0, max: 0, avg: 0, stdDev: 0 };
    }
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    
    const variance = values.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    
    return { min, max, avg, stdDev };
  }

  calculateOperatorStats() {
    const operatorData = {};
    
    this.measurements.forEach(m => {
      const operator = m.operator_name || 'Unknown';
      if (!operatorData[operator]) {
        operatorData[operator] = {
          measurements: [],
          total: 0,
          pass: 0
        };
      }
      
      operatorData[operator].measurements.push(m);
      operatorData[operator].total++;
      
      if (this.getQualityStatus(m) === 'Pass') {
        operatorData[operator].pass++;
      }
    });
    
    Object.keys(operatorData).forEach(operator => {
      const data = operatorData[operator];
      const measurements = data.measurements;
      
      data.passRate = Math.round((data.pass / data.total) * 100);
      
      const validDensity = measurements.filter(m => m.Density !== null && m.Density !== undefined);
      const validCompaction = measurements.filter(m => m.Compaction !== null && m.Compaction !== undefined);
      
      data.avgDensity = validDensity.length > 0 
        ? validDensity.reduce((sum, m) => sum + m.Density, 0) / validDensity.length 
        : 0;
      data.avgCompaction = validCompaction.length > 0 
        ? validCompaction.reduce((sum, m) => sum + m.Compaction, 0) / validCompaction.length 
        : 0;
    });
    
    return operatorData;
  }

  analyzeTimeDistribution() {
    const dates = this.measurements.map(m => {
      const date = new Date(m.LocalTimeOfMeasurement);
      return date.toDateString();
    });
    
    const dayCount = {};
    dates.forEach(date => {
      dayCount[date] = (dayCount[date] || 0) + 1;
    });
    
    const uniqueDays = Object.keys(dayCount);
    const peakEntry = Object.entries(dayCount).reduce((max, entry) => 
      entry[1] > max[1] ? entry : max, ['', 0]);
    
    return {
      totalDays: uniqueDays.length,
      peakDay: peakEntry[0],
      peakCount: peakEntry[1],
      avgPerDay: this.measurements.length / uniqueDays.length
    };
  }

  analyzeLocationCoverage() {
    const coordMeasurements = this.measurements.filter(m => m.latitude && m.longitude);
    
    if (coordMeasurements.length === 0) {
      return { hasCoordinates: false };
    }
    
    const lats = coordMeasurements.map(m => m.latitude);
    const lngs = coordMeasurements.map(m => m.longitude);
    
    return {
      hasCoordinates: true,
      latRange: Math.max(...lats) - Math.min(...lats),
      lngRange: Math.max(...lngs) - Math.min(...lngs),
      centerLat: lats.reduce((sum, lat) => sum + lat, 0) / lats.length,
      centerLng: lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length
    };
  }

  getQualityStatus(measurement) {
    const density = measurement.Density || 0;
    const compaction = measurement.Compaction || 0;
    
    const thresholds = this.options.qualityThresholds;
    
    if (density >= thresholds.density.pass && compaction >= thresholds.compaction.pass) {
      return 'Pass';
    } else if (density >= thresholds.density.warning && compaction >= thresholds.compaction.warning) {
      return 'Warning';
    } else {
      return 'Fail';
    }
  }

  getDateRange() {
    if (this.measurements.length === 0) return 'No data';
    
    const dates = this.measurements.map(m => new Date(m.LocalTimeOfMeasurement));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    if (minDate.toDateString() === maxDate.toDateString()) {
      return minDate.toLocaleDateString();
    }
    
    return `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
  }

  getEquipmentUsed() {
    const hasQRData = this.measurements.some(m => 
      m.notes?.toLowerCase().includes('qr') || 
      m.notes?.toLowerCase().includes('scan')
    );
    return hasQRData ? 'PQI 380+ with QR Code Scanning' : 'PQI 380 with CSV Export';
  }

  getMinValue(parameter) {
    const values = this.measurements
      .map(m => m[parameter])
      .filter(v => v !== null && v !== undefined && !isNaN(v));
    return values.length > 0 ? Math.min(...values) : 0;
  }

  getMaxValue(parameter) {
    const values = this.measurements
      .map(m => m[parameter])
      .filter(v => v !== null && v !== undefined && !isNaN(v));
    return values.length > 0 ? Math.max(...values) : 0;
  }

  // Static method to generate and download report
  static async generateAndDownload(measurements, projectInfo, options = {}) {
    try {
      const generator = new PDFReportGenerator(measurements, projectInfo, options);
      const doc = await generator.generateReport();
      
      const filename = `${(projectInfo.name || 'AsphaltReport').replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Failed to generate PDF report:', error);
      return { success: false, error: error.message };
    }
  }
}

export default PDFReportGenerator;